/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TranscribeCredentialsResponse } from '@/api/types';
import { transcribeErrorText, useTranscribeStream } from '@/speech/useTranscribeStream';
import { useFlowDisplayLanguage } from '../displayLanguage';
import type { ListenResult } from '../types';

type ListenDialogProps = {
  open: boolean;
  stream: MediaStream | null;
  getCredentials: () => Promise<TranscribeCredentialsResponse>;
  playStartSound: () => Promise<void>;
  onResult: (result: ListenResult) => void;
};

const ECHO_GUARD_MS = 500;
const LISTEN_SOUND_GUARD_MS = 1000;
const LISTENING_TIMEOUT_MS = 30_000;

export const normalizeListenAudioLevel = (rms: number) => {
  if (!Number.isFinite(rms) || rms <= 0) return 0;
  return Math.min(1, Math.max(0, (Math.log10(rms) + 4) / 2));
};

export function ListenDialog({
  open,
  stream,
  getCredentials,
  playStartSound,
  onResult,
}: ListenDialogProps) {
  const displayLanguage = useFlowDisplayLanguage();
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const sessionRef = useRef(0);
  const settledRef = useRef(true);
  const captureEnabledRef = useRef(false);
  const startSequenceStartedRef = useRef(false);
  const useLocalMicrophoneRef = useRef(false);
  const guardTimerRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioSinkRef = useRef<HTMLAudioElement>(null);
  const [activeStream, setActiveStream] = useState<MediaStream | null>(null);
  const [transcribeActive, setTranscribeActive] = useState(false);
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const [listening, setListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [partialTranscript, setPartialTranscript] = useState('');

  const clearTimers = useCallback(() => {
    if (guardTimerRef.current !== null) window.clearTimeout(guardTimerRef.current);
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    guardTimerRef.current = null;
    timeoutRef.current = null;
  }, []);

  const stopLocalStream = useCallback(() => {
    const localStream = localStreamRef.current;
    localStreamRef.current = null;
    localStream?.getTracks().forEach((track) => track.stop());
  }, []);

  const stopRemoteAudioSink = useCallback(() => {
    const audio = remoteAudioSinkRef.current;
    if (!audio) return;
    audio.pause();
    audio.srcObject = null;
  }, []);

  const deactivate = useCallback(() => {
    sessionRef.current += 1;
    clearTimers();
    captureEnabledRef.current = false;
    startSequenceStartedRef.current = false;
    useLocalMicrophoneRef.current = false;
    setCaptureEnabled(false);
    setTranscribeActive(false);
    setListening(false);
    setAudioLevel(0);
    setPartialTranscript('');
    setActiveStream(null);
    stopRemoteAudioSink();
    stopLocalStream();
  }, [clearTimers, stopLocalStream, stopRemoteAudioSink]);

  const finishListen = useCallback((result: ListenResult) => {
    if (settledRef.current) return;
    settledRef.current = true;
    deactivate();
    onResultRef.current(result);
  }, [deactivate]);

  const scheduleListeningTimeout = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      finishListen({ status: 'timeout' });
    }, LISTENING_TIMEOUT_MS);
  }, [finishListen]);

  const handleTranscript = useCallback((text: string) => {
    const value = text.trim();
    if (!captureEnabledRef.current || !value) return;
    finishListen({ status: 'success', value });
  }, [finishListen]);

  const handleTranscribeReady = useCallback(() => {
    if (settledRef.current || startSequenceStartedRef.current) return;
    startSequenceStartedRef.current = true;
    const session = sessionRef.current;
    const useLocalMicrophone = useLocalMicrophoneRef.current;

    void (async () => {
      if (!useLocalMicrophone) {
        await playStartSound().catch(() => undefined);
      }
      if (settledRef.current || sessionRef.current !== session) return;

      const guardMs = ECHO_GUARD_MS + (useLocalMicrophone ? 0 : LISTEN_SOUND_GUARD_MS);
      guardTimerRef.current = window.setTimeout(() => {
        guardTimerRef.current = null;
        if (settledRef.current || sessionRef.current !== session) return;
        captureEnabledRef.current = true;
        setCaptureEnabled(true);
        setListening(true);
        scheduleListeningTimeout();
      }, guardMs);
    })();
  }, [playStartSound, scheduleListeningTimeout]);

  useTranscribeStream({
    stream: activeStream,
    active: transcribeActive,
    captureEnabled,
    getCredentials,
    onReady: handleTranscribeReady,
    onTranscript: handleTranscript,
    onPartialTranscript: setPartialTranscript,
    onAudioLevel: (rms) => setAudioLevel(normalizeListenAudioLevel(rms)),
    onVoiceActivity: () => {
      if (captureEnabledRef.current) scheduleListeningTimeout();
    },
    onError: (message) => finishListen({ status: 'error', message }),
  });

  useEffect(() => {
    if (!open) {
      settledRef.current = true;
      deactivate();
      return;
    }

    settledRef.current = false;
    const session = sessionRef.current + 1;
    sessionRef.current = session;
    captureEnabledRef.current = false;
    startSequenceStartedRef.current = false;
    setCaptureEnabled(false);
    setTranscribeActive(false);
    setListening(false);
    setAudioLevel(0);
    setPartialTranscript('');

    void (async () => {
      const useLocalMicrophone = stream === null;
      useLocalMicrophoneRef.current = useLocalMicrophone;
      let selectedStream = stream;
      if (useLocalMicrophone) {
        if (!navigator.mediaDevices?.getUserMedia) {
          finishListen({ status: 'error', message: 'マイクを使用できません' });
          return;
        }
        try {
          selectedStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
          finishListen({ status: 'error', message: 'マイクを使用できません' });
          return;
        }
        if (settledRef.current || sessionRef.current !== session) {
          selectedStream.getTracks().forEach((track) => track.stop());
          return;
        }
        localStreamRef.current = selectedStream;
      }

      if (!selectedStream?.getAudioTracks().some((track) => track.readyState === 'live')) {
        finishListen({
          status: 'error',
          message: useLocalMicrophone
            ? 'マイクを使用できません'
            : 'ロボットのマイク音声を受信できません',
        });
        return;
      }

      if (!useLocalMicrophone) {
        const audio = remoteAudioSinkRef.current;
        if (!audio) {
          finishListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
          return;
        }
        // Start a muted media sink before the AudioWorklet consumes the newly enabled remote track.
        audio.srcObject = selectedStream;
        try {
          await audio.play();
        } catch {
          finishListen({ status: 'error', message: 'ロボットのマイク音声を受信できません' });
          return;
        }
        if (settledRef.current || sessionRef.current !== session) return;
      }

      try {
        await getCredentials();
      } catch (error) {
        finishListen({ status: 'error', message: transcribeErrorText(error) });
        return;
      }
      if (settledRef.current || sessionRef.current !== session) return;

      setActiveStream(selectedStream);
      setTranscribeActive(true);
    })();

    return () => {
      settledRef.current = true;
      deactivate();
    };
  }, [deactivate, finishListen, getCredentials, open, stream]);

  if (!open) return null;

  const title = displayLanguage === 'en' ? 'Listen' : '聞き取り';
  const status = listening
    ? (displayLanguage === 'en' ? 'Speak now' : 'はなしかけてください')
    : (displayLanguage === 'en' ? 'Preparing microphone…' : 'マイクをじゅんびしています…');
  const transcriptLabel = displayLanguage === 'en' ? 'Heard words' : 'ききとったことば';
  const cancelLabel = displayLanguage === 'en' ? 'Cancel' : 'やめる';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 animate-[fade-in_200ms_ease]">
      <audio ref={remoteAudioSinkRef} autoPlay muted aria-hidden="true" className="sr-only" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="listen-dialog-title"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-[pop-in_200ms_ease-out]"
      >
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 id="listen-dialog-title" className="text-lg font-semibold text-slate-800">{title}</h2>
          <p className="mt-1 text-sm text-slate-600" aria-live="polite">{status}</p>
        </div>
        <div className="space-y-5 px-5 py-5">
          <div>
            <div
              role="progressbar"
              aria-label={displayLanguage === 'en' ? 'Microphone level' : 'マイク音量'}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(audioLevel * 100)}
              className="h-4 overflow-hidden rounded-full bg-slate-100"
            >
              <div
                className="h-full rounded-full bg-lime-500 transition-[width] duration-75"
                style={{ width: `${Math.round(audioLevel * 100)}%` }}
              />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {transcriptLabel}
            </p>
            <p className="mt-2 min-h-16 rounded-xl bg-lime-50 px-4 py-3 text-xl font-semibold text-slate-800" aria-live="polite">
              {partialTranscript || '…'}
            </p>
          </div>
        </div>
        <div className="flex justify-end border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={() => finishListen({ status: 'cancelled' })}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
